import React, {useEffect, useRef, useState} from 'react';
import axios from 'axios';
import post from '../utils/post.js';
import FormText from './FormText.js';
import FormTextArea from './FormTextArea.js';
import FormScan from './FormScan.js';
import EditableProfilePhoto from './EditableProfilePhoto.js';
import usePopupAnimation from '../utils/usePopupAnimation.js';
import validateEmail from '../utils/validateEmail.js';

function getProfileInitials(value) {
	const parts = String(value || '')
		.replace(/[^\p{L}\p{N}\s]/gu, ' ')
		.split(/\s+/)
		.filter(Boolean);

	if (parts.length >= 2) {
		return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
	}

	if (parts.length === 1) {
		return parts[0].substring(0, 2).toUpperCase();
	}

	return '';
}

export default function InstructorUserNew({showNew, closeNew, refreshList}) {
	const popupAnimationMs = 300;
	const [loading, setLoading] = useState(false);
	const [searchLoading, setSearchLoading] = useState(false);
	const [addExistingLoading, setAddExistingLoading] = useState(false);
	const [searchEmail, setSearchEmail] = useState('');
	const [searchEmailError, setSearchEmailError] = useState('');
	const [searchPromoCode, setSearchPromoCode] = useState('');
	const [searchPromoCodeError, setSearchPromoCodeError] = useState('');
	const [searchResult, setSearchResult] = useState(null);
	const [email, setEmail] = useState('');
	const [emailError, setEmailError] = useState('');
	const [firstName, setFirstName] = useState('');
	const [firstNameError, setFirstNameError] = useState('');
	const [lastName, setLastName] = useState('');
	const [lastNameError, setLastNameError] = useState('');
	const [nickname, setNickname] = useState('');
	const [age, setAge] = useState('');
	const [myweight, setMyweight] = useState('');
	const [target, setTarget] = useState('');
	const [prefix, setPrefix] = useState('420');
	const [prefixError, setPrefixError] = useState('');
	const [phone, setPhone] = useState('');
	const [phoneError, setPhoneError] = useState('');
	const [formError, setFormError] = useState('');
	const [selectedPhoto, setSelectedPhoto] = useState(null);
	const [selectedPhotoUrl, setSelectedPhotoUrl] = useState('');
	const searchEmailRef = useRef();
	const searchPromoCodeRef = useRef();
	const emailRef = useRef();
	const firstNameRef = useRef();
	const lastNameRef = useRef();
	const prefixRef = useRef();
	const phoneRef = useRef();
	const {shouldRender, isClosing} = usePopupAnimation(showNew, popupAnimationMs);

	useEffect(() => {
		if (!showNew) {
			return;
		}

		setEmail('');
		setSearchEmail('');
		setSearchEmailError('');
		setSearchPromoCode('');
		setSearchPromoCodeError('');
		setSearchResult(null);
		setEmailError('');
		setFirstName('');
		setFirstNameError('');
		setLastName('');
		setLastNameError('');
		setNickname('');
		setAge('');
		setMyweight('');
		setTarget('');
		setPrefix('420');
		setPrefixError('');
		setPhone('');
		setPhoneError('');
		setFormError('');
		setSelectedPhoto(null);
		setSelectedPhotoUrl('');
	}, [showNew]);

	useEffect(() => {
		return () => {
			if (selectedPhotoUrl) {
				URL.revokeObjectURL(selectedPhotoUrl);
			}
		};
	}, [selectedPhotoUrl]);

	const handlePreparedPhoto = (photoUrl, responseData) => {
		setSelectedPhoto(responseData?.blob || null);
		setSelectedPhotoUrl(photoUrl || '');
		setFormError('');
	};

	const uploadPhoto = async (userId) => {
		if (!selectedPhoto || !userId) {
			return;
		}

		const formData = new FormData();
		formData.append('PROFILE_PHOTO', selectedPhoto, 'profilephoto.jpg');
		formData.append('entityKey', String(userId));

		await axios({
			method: 'POST',
			url: '/rest/uploadInstructorProfilePhoto',
			data: formData,
			withCredentials: true,
			headers: {
				'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
			}
		});
	};

	const handleFindExistingUser = () => {
		setSearchEmailError('');
		setSearchPromoCodeError('');
		setSearchResult(null);
		setFormError('');

		if (!searchEmail) {
			setSearchEmailError('povinné');
			searchEmailRef.current?.focus();
			return;
		}

		if (!validateEmail(searchEmail)) {
			setSearchEmailError('Neplatný email');
			searchEmailRef.current?.focus();
			return;
		}

		setSearchLoading(true);

		post('findInstructorUserByEmail', {email: searchEmail})
			.then((res) => {
				setSearchResult(res?.data || null);
				setSearchPromoCode('');
				setSearchLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setSearchEmailError(error?.response?.data?.error || 'Uživatel nenalezen');
				setSearchResult(null);
				setSearchLoading(false);
			});
	};

	const handleVerifyPromoCode = () => {
		setSearchEmailError('');
		setSearchPromoCodeError('');
		setSearchResult(null);
		setFormError('');

		if (!searchPromoCode) {
			setSearchPromoCodeError('Zadejte pozvánku.');
			searchPromoCodeRef.current?.focus();
			return;
		}

		setSearchLoading(true);

		post('findInstructorUserByPromoCode', {promoCode: searchPromoCode})
			.then((res) => {
				setSearchResult(res?.data || null);
				setSearchEmail('');
				setSearchLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setSearchPromoCodeError(error?.response?.data?.error || 'Uživatel nenalezen');
				setSearchResult(null);
				setSearchLoading(false);
			});
	};

	const handleAddExistingUser = () => {
		if (!searchResult?.ID) {
			return;
		}

		setAddExistingLoading(true);
		setSearchEmailError('');
		setFormError('');

		post('addInstructorUser', {id: searchResult.ID})
			.then(() => {
				setSearchEmail('');
				setSearchPromoCode('');
				setSearchEmailError('');
				setSearchPromoCodeError('');
				setSearchResult(null);
				refreshList();
				closeNew();
				setAddExistingLoading(false);
			})
			.catch((error) => {
				console.error(error);
				if (error?.response?.data?.field === 'searchEmail') {
					setSearchEmailError(error?.response?.data?.error || 'Uživatel nebyl nalezen.');
				} else if (error?.response?.data?.field === 'searchPromoCode') {
					setSearchPromoCodeError(error?.response?.data?.error || 'Uživatel nebyl nalezen.');
				} else {
					setFormError(error?.response?.data?.error || 'Přidání existujícího uživatele se nepodařilo.');
				}
				setAddExistingLoading(false);
			});
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		setEmailError('');
		setFirstNameError('');
		setLastNameError('');
		setPrefixError('');
		setPhoneError('');
		setFormError('');

		let hasError = false;

		if (!email) {
			setEmailError('povinné');
			emailRef.current.focus();
			hasError = true;
		} else if (!validateEmail(email)) {
			setEmailError('Neplatný email');
			emailRef.current.focus();
			hasError = true;
		}

		if (!firstName) {
			if (!hasError) {
				firstNameRef.current.focus();
			}
			setFirstNameError('povinné');
			hasError = true;
		}

		if (!lastName) {
			if (!hasError) {
				lastNameRef.current.focus();
			}
			setLastNameError('povinné');
			hasError = true;
		}

		if (!prefix) {
			if (!hasError) {
				prefixRef.current.focus();
			}
			setPrefixError('*');
			hasError = true;
		}

		if (!phone || phone.trim().length < 9) {
			if (!hasError) {
				phoneRef.current.focus();
			}
			setPhoneError('*');
			hasError = true;
		}

		if (hasError) {
			return;
		}

		setLoading(true);

		post('saveInstructorUser', {email, firstName, lastName, nickname, age, myweight, target, prefix, phone})
			.then(async (res) => {
				const userId = parseInt(res?.data?.ID, 10) || 0;

				if (userId > 0 && selectedPhoto) {
					try {
						await uploadPhoto(userId);
					} catch (uploadError) {
						console.error(uploadError);
						setFormError('Uživatel byl vytvořen, ale nahrání fotky se nepodařilo.');
					}
				}

				refreshList();
				closeNew();
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);

				if (error?.response?.data?.field === 'email') {
					setEmailError(error?.response?.data?.error || 'Tento email je již registrován');
					emailRef.current?.focus();
				} else {
					setFormError(error?.response?.data?.error || 'Uložení nového uživatele se nepodařilo.');
				}

				setLoading(false);
			});
	};

	const previewName = `${firstName} ${lastName}`.trim() || nickname || email;
	const searchDisplayName = searchResult ? `${searchResult.LASTNAME || ''} ${searchResult.FIRSTNAME || ''}`.trim() || searchResult.EMAIL : '';
	const searchDisplayInitials = getProfileInitials(`${searchDisplayName}${searchResult?.NICKNAME ? ` ${searchResult.NICKNAME}` : ''}`);

	return shouldRender ? (
		<div className={`popup${isClosing ? ' popup--closing' : ''}`}>
			<div className={`popup_content instructor-user-new__content${isClosing ? ' popup_content--closing' : ''}`}>
				<form method="POST" onSubmit={handleSubmit}>
					<div className="popup_content_in_head">
						<div className="popup_content_in_head_in">Nový klient</div>
						<img alt="" className="popup_content_in_head_in-close" onClick={closeNew} />
					</div>
					<div className="popup_content_in_body instructor-user-new__body">
						<div className="popup_content_in_body_in instructor-user-new__body-in">
							<div className="instructor-user-new__section instructor-user-new__section--search">
								<div className="instructor-user-new__section-head">
									<div className="instructor-user-new__section-title">Vyhledání existujícího uživatele</div>
									<div className="instructor-user-new__section-hint">Zadej celý email existujícího uživatele nebo kód pozvánky.</div>
								</div>
								<div className="instructor-user-new__search-row">
									<div className="instructor-user-new__search-field">
										<FormText type="text" label="EMAIL" value={searchEmail} setValue={setSearchEmail} ref={searchEmailRef} error={searchEmailError} />
									</div>
									<div className="instructor-user-new__search-actions">
										<button type="button" className="button green" onClick={handleFindExistingUser} disabled={searchLoading || addExistingLoading}>
											{searchLoading ? 'Hledám...' : 'Najít'}
										</button>
									</div>
								</div>
								<div className="instructor-user-new__search-row instructor-user-new__search-row--scan">
									<div className="instructor-user-new__search-field">
										<FormScan label="Pozvánka" value={searchPromoCode} setValue={setSearchPromoCode} ref={searchPromoCodeRef} error={searchPromoCodeError} />
									</div>
									<div className="instructor-user-new__search-actions">
										<button type="button" className="button green" onClick={handleVerifyPromoCode} disabled={searchLoading || addExistingLoading}>
											{searchLoading ? 'Ověřuji...' : 'Ověřit'}
										</button>
									</div>
								</div>
								{searchResult ? (
									<div className="instructor-user-new__search-result-wrap">
										<div className="instructor-user-new__search-result">
											<div className="instructor-user-new__search-result-avatar">
												{searchResult.PROFILE_PHOTO_URL ? (
													<img src={searchResult.PROFILE_PHOTO_URL} alt={searchDisplayName || searchResult.EMAIL} className="instructor-user-new__search-result-image" />
												) : (
													<span>{searchDisplayInitials || 'U'}</span>
												)}
											</div>
											<div className="instructor-user-new__search-result-content">
												<div className="instructor-user-new__search-result-name">
													{searchDisplayName || searchResult.EMAIL}
													{searchResult.NICKNAME ? ` (${searchResult.NICKNAME})` : ''}
												</div>
												<div className="instructor-user-new__search-result-meta">{searchResult.EMAIL}</div>
												{searchResult.PHONES ? <div className="instructor-user-new__search-result-meta">{searchResult.PHONES}</div> : null}
												{searchResult.ALREADY_ASSIGNED ? <div className="instructor-user-new__search-result-warning">Uživatel je již přiřazen.</div> : null}
											</div>
										</div>
										{!searchResult.ALREADY_ASSIGNED ? (
											<div className="instructor-user-new__search-result-actions">
												<button type="button" className="button green" onClick={handleAddExistingUser} disabled={searchLoading || addExistingLoading}>
													{addExistingLoading ? 'Přidávám...' : 'Přidat'}
												</button>
											</div>
										) : null}
									</div>
								) : null}
							</div>

							<div className="instructor-user-new__section instructor-user-new__section--create">
								<div className="instructor-user-new__section-head">
									<div className="instructor-user-new__section-title">Nový uživatel</div>
									<div className="instructor-user-new__section-hint">Pokud uživatel neexistuje, vyplň údaje níže a vytvoř nový účet.</div>
								</div>
							<div className="instructor-user-new__photo-panel">
								<EditableProfilePhoto
									photoUrl={selectedPhotoUrl}
									displayName={previewName}
									canEditPhoto={true}
									onPreparedPhoto={handlePreparedPhoto}
									avatarWidth="6rem"
									avatarHeight="6rem"
									texts={{
										selectSourceHeader: 'Profilová fotka',
										selectSourceMessage: 'Vyber zdroj nové profilové fotky.',
										savePhotoButton: 'Použít fotku',
										savingPhotoButton: 'Připravuji...',
										previewUploadButton: selectedPhoto ? 'Změnit fotku' : 'Nahrát fotku'
									}}
								/>
								<div className="instructor-user-new__photo-copy">
									<div className="instructor-user-new__photo-title">Profilová fotka</div>
									<div className="instructor-user-new__photo-hint">Klikni na fotku pro výběr a ořez ze zařízení nebo z fotoaparátu.</div>
								</div>
							</div>

							<FormText type="text" label="EMAIL" value={email} setValue={setEmail} ref={emailRef} error={emailError} />

							<div className="instructor-user-new__grid instructor-user-new__grid--two">
								<div>
									<FormText type="text" label="JMÉNO" value={firstName} setValue={setFirstName} ref={firstNameRef} error={firstNameError} />
								</div>
								<div>
									<FormText type="text" label="PŘÍJMENÍ" value={lastName} setValue={setLastName} ref={lastNameRef} error={lastNameError} />
								</div>
							</div>

							<div className="instructor-user-new__grid instructor-user-new__grid--three">
								<div>
									<FormText type="text" label="PŘEZDÍVKA" value={nickname} setValue={setNickname} error="" />
								</div>
								<div>
									<FormText type="text" label="PŘEDV." value={prefix} setValue={setPrefix} ref={prefixRef} error={prefixError} maxLength={4} />
								</div>
								<div>
									<FormText type="text" label="TELEFON" value={phone} setValue={setPhone} ref={phoneRef} error={phoneError} maxLength={9} />
								</div>
							</div>

							<div className="instructor-user-new__grid instructor-user-new__grid--two">
								<div>
									<FormText type="number" label="VĚK" value={age} setValue={setAge} error="" />
								</div>
								<div>
									<FormText type="number" label="HMOTNOST" value={myweight} setValue={setMyweight} error="" />
								</div>
							</div>

							<FormTextArea type="text" label="CÍL KLIENTA" value={target} setValue={setTarget} error="" height="8rem" />

							{formError !== '' ? <div className="instructor-user-new__error">{formError}</div> : null}
							</div>
						</div>
					</div>
					<div className="popup_content_in_foot">
						<div className="popup_content_in_foot_in">
							<input type="submit" name="button_save" value="Uložit" className="button green mr_2" disabled={loading} />
							<button type="button" className="button" onClick={closeNew}>Zpět</button>
						</div>
					</div>
				</form>
			</div>
		</div>
	) : '';
}